import React, { useEffect, useRef, useState } from 'react';
import Eyebrow from '../motion/Eyebrow';
import KineticHeadline from '../motion/KineticHeadline';
import Reveal from '../motion/Reveal';
import LoopVideo from '../motion/LoopVideo';
import SectionLight from '../motion/SectionLight';

// Every line here is something the app does today.
const STEPS = [
  {
    video: 'feature-pools',
    eyebrow: 'Create or join',
    title: 'Start a pool.\n*Send the code.*',
    body: 'Create a private pool in a minute and share its invite code or link, or jump straight into the free public Title Shot pool. Join as many pools as you like.',
    points: ['Invite codes & links', 'Public or private', 'Unlimited pools'],
  },
  {
    video: 'feature-picks',
    eyebrow: 'Make your picks',
    title: 'Call the winner.\n*Call the finish.*',
    body: 'Pick the winner of every fight, then how it ends: KO/TKO, submission or decision. See how the community is picking each bout before you lock in. Picks close when the card starts.',
    points: ['Winner + method', 'Community pick %', 'Locks at the first bell'],
  },
  {
    video: 'feature-leaderboard',
    eyebrow: 'Chase the leaderboard',
    title: 'Climb the\n*leaderboard.*',
    body: 'Points land as the results come in, and everyone’s picks are revealed once the card locks. Watch the live leaderboard move fight by fight and see exactly who called it.',
    points: ['Live leaderboard', 'Picks revealed at lock', 'Points fight by fight'],
  },
];

/**
 * HOW IT WORKS: a scroll story. On desktop the copy scrolls on the left
 * while one big feathered video stays pinned on the right and dissolves to
 * the step you're reading (only that one plays). On phones each step shows
 * its own video above its copy.
 */
export default function HowItWorks() {
  const [active, setActive] = useState(0);
  const [desktop, setDesktop] = useState(() => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches);
  const stepRefs = useRef([]);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const update = () => setDesktop(mq.matches);
    update();
    if (mq.addEventListener) mq.addEventListener('change', update);
    else mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', update);
      else mq.removeListener(update);
    };
  }, []);

  useEffect(() => {
    let raf = 0;
    const update = () => {
      raf = 0;
      const mid = window.innerHeight / 2;
      let best = 0;
      let bestDist = Infinity;
      stepRefs.current.forEach((el, i) => {
        if (!el) return;
        const r = el.getBoundingClientRect();
        const d = Math.abs(r.top + r.height / 2 - mid);
        if (d < bestDist) { bestDist = d; best = i; }
      });
      setActive(best);
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section id="how" className="pnp-section" style={{ paddingBottom: 'clamp(40px, 6vw, 80px)' }} aria-labelledby="pnp-how-title">
      <SectionLight bleed="80px">
        <div className="pnp-glow" style={{ right: '-30%', top: '18%' }} />
        <div className="pnp-glow" style={{ right: '-35%', top: '62%', opacity: 0.6 }} />
      </SectionLight>
      <div className="pnp-wrap">
        <div className="pnp-head">
          <Eyebrow>How it works</Eyebrow>
          <div id="pnp-how-title">
            <KineticHeadline text={'Three steps to\n*fight night.*'} />
          </div>
        </div>

        <div className="pnp-story">
          <div>
            {STEPS.map((s, i) => (
              <div key={s.video} ref={(el) => { stepRefs.current[i] = el; }} className="pnp-story-step">
                {!desktop ? (
                  <div className="pnp-story-inline">
                    <LoopVideo name={s.video} small feather="ui" label={s.title.replace(/[*\n]/g, ' ')} />
                  </div>
                ) : null}
                <Reveal>
                  <div className={`pnp-story-copy${desktop && active !== i ? ' is-dim' : ''}`}>
                    <span className="pnp-outline-num pnp-story-num" aria-hidden="true">{String(i + 1).padStart(2, '0')}</span>
                    <div className="pnp-eyebrow"><span className="pnp-eyebrow-claw" aria-hidden="true"><i /><i /><i /></span><span>{s.eyebrow}</span></div>
                    <KineticHeadline as="h3" className="pnp-display pnp-h3" text={s.title} />
                    <p className="pnp-lead">{s.body}</p>
                    <div className="pnp-story-points">
                      {s.points.map((p) => <span key={p} className="pnp-chip"><span className="pnp-chip-dot" aria-hidden="true" />{p}</span>)}
                    </div>
                  </div>
                </Reveal>
              </div>
            ))}
          </div>

          {desktop ? (
            <div className="pnp-story-pin" aria-hidden="false">
              <div className="pnp-story-pin-inner">
                <div className="pnp-story-pin-stage">
                  {STEPS.map((s, i) => (
                    <LoopVideo
                      key={s.video}
                      name={s.video}
                      feather="ui"
                      active={active === i}
                      label={s.title.replace(/[*\n]/g, ' ')}
                      style={{ opacity: active === i ? 1 : 0, transform: `scale(${active === i ? 1 : 0.95})` }}
                    />
                  ))}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
